import { inject, Injectable } from '@angular/core';
import { interval, Observable, Subject, forkJoin, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Authorization, AuthorizationInterface, AuthorizationMapper } from 'src/app/model/Authorization';
import { TransactionEvent, TransactionEventInterface, TransactionEventMapper, TransactionEventStatus } from 'src/app/model/TransactionEvent';
import { AppConstants } from 'src/app/constants/app-constants';
import { UserService } from '../user/user.service';
import { User } from 'src/app/model/User';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import {
    TRANSACTION_CAPTURE_ENDPOINT,
    TRANSACTION_VOID_ENDPOINT,
    TRANSACTION_REFUND_ENDPOINT,
    TRANSACTION_EVENTS_ENDPOINT,
    AUTHORIZATION_BY_COMPANY_ENDPOINT,
    TRANSACTION_REFUND_LOANLEVEL_ENDPOINT,
    TRANSACTION_EVENT_DETAIL_ENDPOINT
} from 'src/app/constants/api-routes';
import { TransactionRequest } from 'src/app/model/TransactionRequest';
import { HandleTransactionRequestResponse } from '../transaction-request/transaction-request.service';

/** Data object provided to capture endpoint */
export interface CaptureData {
    amount: number;
    complete: boolean;
    planId: string;
    memo: string;
}

/** Data object provided to void endpoint */
export interface VoidData {
    amount: number;
    memo?: string;
}

/** Data object provided to refund endpoint */
export interface RefundData {
  memo?: string;
}

export interface RefundLoanLevelData extends RefundData {
  amount: number; // required here
}

/** Api return types */
export type CaptureReturnType = { transaction_id: string, preauth_id: string, session_id: string, amount: number, status: string };
export type VoidReturnType = { preauth_id: string, void_amount: number, status: string }; // Status = 'Voided' (hardcoded in backend)

/**
 * AuthorizationService handles data operations with Authorizations and Transaction Events
 */
@Injectable({
    providedIn: 'root'
})
export class AuthorizationService {
    /** Injected services */
    private _userService: UserService = inject(UserService);
    private _authService: AuthService = inject(AuthService);
    private _http: HttpClient = inject(HttpClient);

    /** Subjects and observables */
    private _authorizationsSub = new Subject<Authorization[]>();
    public authorizations$ = this._authorizationsSub.asObservable();
    private _transactionEventsSub = new Subject<TransactionEvent[]>();
    public transactionEvents$ = this._transactionEventsSub.asObservable();

    /** Data collections */
    private _authorizationCollection?: Authorization[];
    private _transactionEventCollection?: TransactionEvent[];

    /** An observable to refresh API data */
    public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
    private _timers: number[] = [0];

    /**
     * Default constructor for the service, used to initialize services
     *
     * @param _userService
     * @param _authService
     * @param _http
     */
    constructor() {
        // On authentication changes, clear persisted data
        this._userService.user$.subscribe((user: User | null) => {
            if (this._authService.authenticated && user != undefined) {
                this._fetch();
            } else {
                this._clear();
            }
        });

        // Continuously query the backend to receive new authorizations
        this._startTimer(0);
        this.timer.subscribe((x: number) => {
            this._timers.splice(this._timers.indexOf(x));
            this._startTimer(x + 1);
            this._fetch();
        });
    }

    /** Base Collection Operations */

    /**
     * Get a list of authorizations
     *
     * @return A reference to the current collection of authorizations, or undefined if not fetched yet
     */
    public getAuthorizations(): Authorization[] | undefined {
        return this._authorizationCollection?.map(x => x.copyOf());
    }


    /**
     * Get a list of transaction events
     *
     * @return A reference to the current collection of transaction events, or undefined if not fetched yet
     */
    public getTransactionEvents(): TransactionEvent[] | undefined {
        return this._transactionEventCollection?.map(x => x.copyOf());
    }

    public refreshAuthorizationsAndEvents(): void {
        this._fetch();
    }

    /**
     * Add a transaction event to an authorization
     * 
     * @param transactionId ID of the transaction event (used to find authorization)
     * @param event The transaction event to add
     */
    public addTransactionEventToAuthorization(transactionId: string, event: TransactionEvent): void {
        // Find the authorization that contains this transaction event
        const authorization = this._authorizationCollection?.find(a => a.hasTransactionEvent(transactionId));
        if (!authorization) {
            throw new Error(`Authorization with transaction ID '${transactionId}' not found`);
        }

        // Add the transaction event to the transaction event collection
        this._refreshTransactionEvents(event, 'create');

        // Add the transaction event to the authorization and modify the authorization collection
        authorization.addTransactionEvent(event);
        this._refreshAuthorizations(authorization, 'modify');
    }

    /** Operations (capture, void, refund, etc) */

    /**
     * Capture a certain amount on an authorization
     * 
     * @param authorizationId The ID of the authorization to capture
     * @param captureData
     * @returns Observable of transaction event creation
     */
    public captureAuthorization(authorizationId: string, captureData: CaptureData): Observable<TransactionEvent> {
        // Make sure authorization exists
        const authorization = this._authorizationCollection?.find(a => a.id === authorizationId);
        if (!authorization) {
            throw new Error(`Authorization with ID '${authorizationId}' not found`);
        }

        // Validate capture data specifics
        if (!captureData) {
            throw new Error(`Capture data is required`);
        }
        if (captureData.amount <= 0 || captureData.amount > authorization.remainingAmount) {
            throw new Error(`Capture amount must be greater than 0 and less than or equal to the authorization's remaining amount`);
        }

        const companyId = this._userService.company?.companyId;
        if (!companyId) {
            throw new Error('Company information is missing');
        }

        // Perform HTTP request
        const url = TRANSACTION_CAPTURE_ENDPOINT(companyId, authorizationId);

        return this._http.post<TransactionEventInterface>(url, captureData).pipe(
            map((intf: TransactionEventInterface) => {
                // Update transaction event
                const transaction = TransactionEventMapper.toModel(intf);
                this._refreshTransactionEvents(transaction, 'create');

                // Update authorization
                authorization.addTransactionEvent(transaction);
                authorization.captured_amount += captureData.amount;
                this._refreshAuthorizations(authorization, 'modify');

                // Return the new transaction event
                return transaction;
            })
            /*
         return this._http.post<CaptureReturnType>(url, captureData).pipe(
             map((resp: CaptureReturnType) => {
                 const intf = {
                     // Unknowns
                     buyer_id: '',
                     created_at: new Date().toISOString(),
                     last_updated_at: new Date().toISOString(),
 
                     // Known from context
                     id: 'Pending Capture',
                     buyer_company: authorization.buyer_company,
                     supplier_id: authorization.supplier_id,
                     status: 'Captured' as TransactionEventStatus,
                     amount: captureData.amount, // Amount returned in resp is authorization capture total?
 
                     // Known from response
                     transaction_id: resp.transaction_id,
                     accepted_tx_id: resp.preauth_id,
                     tx_request_id: resp.session_id,
                 }
 
                 // Create new event
                 const newEvent = TransactionEventMapper.toModel(intf);
                 this._refreshTransactionEvents(newEvent, 'create');
 
                 // Add to authorization
                 authorization.captured_amount += captureData.amount;
                 authorization.addTransactionEvent(newEvent);
                 this._refreshAuthorizations(authorization, 'modify');
 
                 return newEvent;
             }),
             */
        );
    }

    /**
     * Void an amount on an authorization
     * 
     * @param authorizationId The ID of the authorization to void
     * @param voidData
     * @returns Observable of transaction event creation
     */
    public voidAuthorization(authorizationId: string, voidData: VoidData): Observable<TransactionEvent> {
        // Make sure authorization exists
        const authorization = this._authorizationCollection?.find(a => a.id === authorizationId);
        if (!authorization) {
            throw new Error(`Authorization with ID '${authorizationId}' not found`);
        }

        // Validate void data specifics
        if (!voidData) {
            throw new Error(`Void data is required`);
        }
        if (voidData.amount <= 0 || voidData.amount > authorization.remainingAmount) {
            throw new Error(`Void amount must be greater than 0 and less than or equal to the authorization's remaining amount`);
        }

        const companyId = this._userService.company?.companyId;
        if (!companyId) {
            throw new Error('Company information is missing');
        }

        // Perform HTTP request
        const url = TRANSACTION_VOID_ENDPOINT(companyId, authorizationId);

        return this._http.post<TransactionEventInterface>(url, voidData).pipe(
            map((intf: TransactionEventInterface) => {
                // Update transaction event
                const transaction = TransactionEventMapper.toModel(intf);
                this._refreshTransactionEvents(transaction, 'create');

                // Update authorization
                authorization.addTransactionEvent(transaction);
                authorization.voided_amount += voidData.amount;
                this._refreshAuthorizations(authorization, 'modify');

                // Return the new transaction event
                return transaction;
            })
            /*
         return this._http.post<VoidReturnType>(url, voidData).pipe(
             map((resp: VoidReturnType) => {
                 const intf = {
                     // Unknowns
                     buyer_id: '',
                     created_at: new Date().toISOString(),
                     last_updated_at: new Date().toISOString(),
                     transaction_id: '',
                     tx_request_id: '',
 
                     // Known from context
                     id: 'Pending Void',
                     buyer_company: authorization.buyer_company,
                     supplier_id: authorization.supplier_id,
                     status: 'Voided' as TransactionEventStatus,
 
                     // Known from response
                     amount: resp.void_amount,
                     accepted_tx_id: resp.preauth_id,
                 }
 
                 // Create new event
                 const newEvent = TransactionEventMapper.toModel(intf);
                 this._refreshTransactionEvents(newEvent, 'create');
 
                 // Add to authorization
                 authorization.voided_amount += voidData.amount;
                 authorization.addTransactionEvent(newEvent);
                 this._refreshAuthorizations(authorization, 'modify');
 
                 return newEvent;
             }),
             */
        );
    }

    /**
     * Refund an amount on an authorization
     * 
     * @param authorizationId The ID of the authorization to refund
     * @param refundData
     * @returns Observable of transaction event creation
     */
/**
 * Refund an authorization in full (backend uses captured_amount; we only send memo)
 */
public refundAuthorization(authorizationId: string, refundData: RefundData): Observable<TransactionEvent> {
  // Make sure authorization exists
  const authorization = this._authorizationCollection?.find(a => a.id === authorizationId);
  if (!authorization) {
    throw new Error(`Authorization with ID '${authorizationId}' not found`);
  }

  // Basic presence check
  if (!refundData) {
    throw new Error(`Refund data is required`);
  }

  const companyId = this._userService.company?.companyId;
  if (!companyId) {
    throw new Error('Company information is missing');
  }

  const url = TRANSACTION_REFUND_ENDPOINT(companyId, authorizationId);

  return this._http.post<TransactionEventInterface>(url, refundData).pipe(
    map((intf: TransactionEventInterface) => {
      const transaction = TransactionEventMapper.toModel(intf);
      this._refreshTransactionEvents(transaction, 'create');

      // Attach event to authorization
      authorization.addTransactionEvent(transaction);

      // If backend returns a positive refund amount, subtract it from captured_amount.
      // (Adjust this if your refunds come back as negative amounts.)
      authorization.captured_amount -= transaction.amount;

      this._refreshAuthorizations(authorization, 'modify');

      return transaction;
    }),
  );
}


    /**
 * Refund an amount on an authorization
 * 
 * @param authorizationId The ID of the authorization to refund
 * @param refundData
 * @returns Observable of transaction event creation
 */
public refundCapture(
  authorizationId: string,
  refundData: RefundLoanLevelData,
  acctrefno: string
): Observable<{ refund: TransactionEvent; updatedCapture: TransactionEvent | null }> {
        // Make sure authorization exists
        const authorization = this._authorizationCollection?.find(a => a.id === authorizationId);
        if (!authorization) {
            throw new Error(`Capture with ID '${authorizationId}' not found`);
        }

        // Validate refund data specifics
        if (!refundData) {
            throw new Error(`Refund data is required`);
        }
        if (refundData.amount <= 0 || refundData.amount > authorization.captured_amount) {
            throw new Error(`Refund amount must be greater than 0 and less than or equal to the authorization's captured amount`);
        }

        const companyId = this._userService.company?.companyId;
        if (!companyId) {
            throw new Error('Company information is missing');
        }

        const url = TRANSACTION_REFUND_LOANLEVEL_ENDPOINT(companyId, authorizationId, acctrefno);

        return this._http.post<TransactionEventInterface>(url, refundData).pipe(
  map((intf: TransactionEventInterface) => {
    // New refund event from backend
    const refundTx = TransactionEventMapper.toModel(intf);
    this._refreshTransactionEvents(refundTx, 'create');

    // 🔹 Find the original CAPTURE event for this loan / acctrefno
    const eventsForAuth = authorization.getTransactionEvents();
    const captureEvent = eventsForAuth.find(e =>
      (e as any).acctrefno === acctrefno && e.status === 'captured'
    );

    if (captureEvent) {
      // 🔹 Recompute total refunded against this capture
      const totalRefundedForThisCapture =
        eventsForAuth
          .filter(ev =>
            (ev as any).acctrefno === acctrefno &&
            ev.id !== captureEvent.id &&
            ev.amount < 0 // assuming refunds are negative amounts
          )
          .reduce((sum, ev) => sum + Math.abs(ev.amount), 0) +
        refundData.amount; // include this new refund

      (captureEvent as any).remaining_amount =
        captureEvent.amount - totalRefundedForThisCapture;

      if ((captureEvent as any).remaining_amount < 0) {
        (captureEvent as any).remaining_amount = 0;
      }

      // 🔹 Push updated capture into collections so subscribers see it
      this._refreshTransactionEvents(captureEvent, 'modify');

      // If Authorization holds its own copy:
      authorization.setTransactionEvents(
        eventsForAuth.map(ev => (ev.id === captureEvent.id ? captureEvent : ev))
      );
    }

    // 🔹 Update authorization totals
    authorization.addTransactionEvent(refundTx);
    authorization.captured_amount -= refundData.amount;
    this._refreshAuthorizations(authorization, 'modify');

    // 🔹 Return both refund + updated capture
    return {
      refund: refundTx,
      updatedCapture: captureEvent ?? null,
    };
  }),
);

    }

    public getTransactionEventById(eventId: string): Observable<TransactionEvent> {
  const companyId = this._userService.company?.companyId;
  if (!companyId) {
    throw new Error('Company information is missing');
  }

  const url = TRANSACTION_EVENT_DETAIL_ENDPOINT(companyId, eventId);

  return this._http
    .get<TransactionEventInterface>(url)
    .pipe(map((intf) => TransactionEventMapper.toModel(intf)));
}



    /**
     * Create an authorization from a transaction request
     * 
     * @param request The transaction request to create an authorization from
     * @param createResponse The response from the transaction request creation
     */
    public createAuthorizationFromRequest(request: TransactionRequest, createResponse: HandleTransactionRequestResponse): void {
        // Make sure the request was accepted
        if (request.status !== 'accepted') {
            throw new Error(`Cannot create authorization from request with status '${request.status}'`);
        }

        if (!request.tx_request_id) {
            throw new Error('Transaction request is missing session ID');
        }

        if (!createResponse.preauth_id) {
            throw new Error('Create response is missing preauth ID');
        }

        // Create a new authorization from the request
        const authorization = new Authorization(
            createResponse.preauth_id,
            request.buyer_company,
            request.buyer_id,
            request.supplier_id,
            request.supplier_company,
            new Date(),
            new Date(),
            0,
            0,
            request.amount,
            request.tx_request_id,
        );

        this._refreshAuthorizations(authorization, 'create');
    }

    /** Helper methods */

    /**
     * Helper function to set current collections
     *
     * @param authorizations
     * @param transactionEvents
     */
    private _set(authorizations: Authorization[], transactionEvents: TransactionEvent[]): void {
        const authorizationCopy = authorizations.map(x => x.copyOf());
        this._authorizationCollection = authorizationCopy;
        this._authorizationsSub.next(authorizationCopy);

        const transactionEventsCopy = transactionEvents.map(x => x.copyOf());
        this._transactionEventCollection = transactionEventsCopy;
        this._transactionEventsSub.next(transactionEventsCopy);
    }

    /**
     * Helper function to clear current collections
     */
    private _clear(): void {
        this._authorizationCollection = [];
        this._transactionEventCollection = [];
        this._authorizationsSub.next([]);
        this._transactionEventsSub.next([]);
    }

    /**
     * Get authorizations and transaction events from the backend and update respective collections. This method
     * also links transaction events to their associated authorizations
     */
    private _fetch(): void {
        console.log('[AUTH]: _fetch() start');
        // Make sure the user is authenticated
        if (!this._authService.authenticated) return;

        // Get the user's active company
        const company = this._userService.company;
        if (company == undefined) {
            throw new Error("user is not part of any company")
        };

        // Get companyId and company type
        const role = company.companyType?.toLowerCase() as 'borrower' | 'supplier';
        const companyId = company.companyId;
        if (role == undefined || companyId == undefined) {
            throw new Error(`User has invalid company type '${role}' or company id '${companyId}'`);
        };

        // Fetch both transaction events and authorizations in parallel
        const transactionEvents$ = this._http.get<TransactionEventInterface[]>(TRANSACTION_EVENTS_ENDPOINT(companyId, role));
        const authorizations$ = this._http.get<AuthorizationInterface[]>(AUTHORIZATION_BY_COMPANY_ENDPOINT(companyId, role));

        forkJoin({
            transactionEvents: transactionEvents$,
            authorizations: authorizations$
        }).subscribe({
            next: (result): void => {
                      console.log('[AUTH]: _fetch() success, events:', result.transactionEvents?.length, 'auths:', result.authorizations?.length);

                if (result.transactionEvents == undefined || result.authorizations == undefined) {
                    console.error(`[TRANSACTION-SERVICE]: cannot cast backend output into authorization or transaction event lists`);
                    return;
                }

                // Convert transaction events to models
                let transactionEvents: TransactionEvent[] = [];
                for (let transactionEventInt of result.transactionEvents) {
                    transactionEvents.push(TransactionEventMapper.toModel(transactionEventInt));
                }

                // Convert authorizations to models that contain their transaction events
                let authorizations: Authorization[] = [];
                for (let authorizationInt of result.authorizations) {
                    const model = AuthorizationMapper.toModel(authorizationInt);
                    model.setTransactionEvents(transactionEvents.filter(t => authorizationInt.id === t.accepted_tx_id));

                    authorizations.push(model);
                }

                // Propagate changes
                this._set(authorizations, transactionEvents);
            },
            error: (err) => {
                console.error(`[TRANSACTION-SERVICE]: failed to fetch authorizations and transaction events`, err);
            }
        });
    }

    /**
     * Start the last updated time from 0
     */
    private async _startTimer(index: number) {
        this._timers.push(index);

        let seconds = 0;
        let minutes = 0;

        while (this._timers.includes(index)) {
            // Increment seconds
            await function (delay: number): Promise<void> { return new Promise(r => { setTimeout(r, delay) }) }(1000);
            seconds++;

            // Increment minutes
            if (seconds >= 60) {
                seconds = 0;
                minutes++;
            }
        }
    }

    /**
     * Adds, modifies or deletes an authorization from the entity collection (doesn't mutate)
     *
     * @param entityToSave The entity to be added, modified, or deleted
     * @param action The action to perform ('create', 'modify', or 'delete').
     *               Defaults to 'create'.
     */
    private _refreshAuthorizations(entityToSave: Authorization, action: ('create' | 'modify' | 'delete') = 'create'): void {
        if (this._authorizationCollection) {
            if (action === 'create') {
                this._authorizationCollection = [...this._authorizationCollection.map(entity => entity.copyOf()), entityToSave];
            } else if (action === 'modify') {
                this._authorizationCollection = this._authorizationCollection.map(entity => entity.id === entityToSave.id ? entityToSave.copyOf() : entity.copyOf());
            } else if (action === 'delete') {
                this._authorizationCollection = this._authorizationCollection.filter(entity => entity.id !== entityToSave.id);
            }
            this._authorizationsSub.next(this._authorizationCollection.map(entity => entity.copyOf()));
        }
    }

    /**
     * Adds, modifies or deletes a posted transaction from the entity collection (doesn't mutate)
     *
     * @param entityToSave The entity to be added, modified, or deleted
     * @param action The action to perform ('create', 'modify', or 'delete').
     *               Defaults to 'create'.
     */
    private _refreshTransactionEvents(entityToSave: TransactionEvent, action: ('create' | 'modify' | 'delete') = 'create'): void {
        if (this._transactionEventCollection) {
            if (action === 'create') {
                this._transactionEventCollection = [...this._transactionEventCollection.map(entity => entity.copyOf()), entityToSave];
            } else if (action === 'modify') {
                this._transactionEventCollection = this._transactionEventCollection.map(entity => entity.id === entityToSave.id ? entityToSave.copyOf() : entity.copyOf());
            } else if (action === 'delete') {
                this._transactionEventCollection = this._transactionEventCollection.filter(entity => entity.id !== entityToSave.id);
            }
            this._transactionEventsSub.next(this._transactionEventCollection.map(entity => entity.copyOf()));
        }
    }
}